/* ----------------------------------------------------------
   MessageInput.jsx — WHATSAPP STYLE + TOOLBAR + MULTI FILE
   + FULL REPLY SUPPORT + DRAG & DROP
----------------------------------------------------------- */

import { useEffect, useRef, useState } from "react";
import { useChatStore } from "../store/useChatStore";
import { SERVER_URL } from "../api";
import { Send, X, FileText, Paperclip, Smile } from "lucide-react";
import toast from "react-hot-toast";
import api from "../api";
/* ----------------- Emoji Small List ----------------- */
const EMOJI_BANK = [
  "👍",
  "😊",
  "😉",
  "🙏",
  "🤝",
  "✅",
  "📌",
  "⏰",
  "📝",
  "😀",
  "😃",
  "😄",
  "😅",
  "📋",
  "📊",
  "⏳",
  "📞",
  "📵",
  "⭐",
  "⚠️",
];
/* ----------- Cursor Position Helper ----------- */
function getCaretCoords(textarea, pos) {
  const div = document.createElement("div");
  const style = window.getComputedStyle(textarea);

  for (let p of style) div.style[p] = style[p];

  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";

  const text = textarea.value.substring(0, pos);
  const after = textarea.value.substring(pos) || ".";

  div.textContent = text;

  const span = document.createElement("span");
  span.textContent = after;
  div.appendChild(span);

  textarea.parentElement.appendChild(div);
  const rect = span.getBoundingClientRect();
  div.remove();

  return rect;
}

/* ----------------------------------------------------------
     FLOATING TOOLBAR
----------------------------------------------------------- */
function FloatingToolbar({ text, setText, textareaRef }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const apply = (startTag, endTag) => {
    const t = textareaRef.current;
    const s = t.selectionStart;
    const e = t.selectionEnd;

    const selected = text.slice(s, e);
    const newText =
      text.slice(0, s) + startTag + selected + endTag + text.slice(e);

    setText(newText);

    setTimeout(() => {
      t.focus();
      t.selectionStart = s + startTag.length;
      t.selectionEnd = e + startTag.length;
    }, 10);
  };

  const updateToolbar = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    const s = ta.selectionStart;
    const e = ta.selectionEnd;

    if (s === e) return setVisible(false);

    const rect = getCaretCoords(ta, e);

    setPos({
      top: rect.top - 45,
      left: Math.max(rect.left - 80, 12),
    });

    setVisible(true);
  };

  useEffect(() => {
    const t = textareaRef.current;
    if (!t) return;

    t.addEventListener("mouseup", updateToolbar);
    t.addEventListener("keyup", updateToolbar);

    return () => {
      t.removeEventListener("mouseup", updateToolbar);
      t.removeEventListener("keyup", updateToolbar);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        padding: "6px 10px",
        borderRadius: 12,
        background: "#fff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        display: "flex",
        gap: 10,
        zIndex: 500,
      }}
    >
      <button onClick={() => apply("**", "**")}>
        <b>B</b>
      </button>
      <button onClick={() => apply("*", "*")}>
        <i>I</i>
      </button>
      <button onClick={() => apply("~", "~")}>S</button>
      <button onClick={() => apply("```", "```")}>{"</>"}</button>
      <button onClick={() => apply("- ", "\n")}>•</button>
      <button onClick={() => apply("1. ", "\n")}>1.</button>
      <button onClick={() => apply("> ", "\n")}>&gt;</button>
    </div>
  );
}

/* ----------------------------------------------------------
              MAIN INPUT COMPONENT
----------------------------------------------------------- */
export default function MessageInput({ replyTo, setReplyTo }) {
  const { sendMessage, selectedUser } = useChatStore();

  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);

  const textareaRef = useRef();
  const fileRef = useRef();
  /* ----------------- Paste Image Support (WhatsApp Style) ----------------- */
  const handlePaste = (e) => {
    if (!e.clipboardData) return;

    const items = e.clipboardData.items;
    if (!items) return;

    const images = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf("image") !== -1) {
        const blob = item.getAsFile();
        if (blob) {
          const file = new File([blob], "pasted-image.png", {
            type: blob.type,
          });
          images.push(file);
        }
      }
    }

    if (images.length > 0) {
      e.preventDefault(); // stop text paste
      addFiles(images);
    }
  };

  /* ----------------- Drag & Drop (NEW) ----------------- */
  const dropRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  /* ----------------- WhatsApp Enter Behaviour ----------------- */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && window.innerWidth >= 768) {
      if (e.altKey) {
        e.preventDefault();
        const ta = textareaRef.current;
        const s = ta.selectionStart;
        const ePos = ta.selectionEnd;
        const newT = text.slice(0, s) + "\n" + text.slice(ePos);
        setText(newT);

        setTimeout(() => {
          ta.selectionStart = ta.selectionEnd = s + 1;
          autoResize();
        }, 10);
      } else {
        e.preventDefault();
        handleSend();
      }
      return;
    }

    if (e.ctrlKey && e.key.toLowerCase() === "b") {
      e.preventDefault();
      applyShortcutFormat("**", "**");
    }

    if (e.ctrlKey && e.key.toLowerCase() === "i") {
      e.preventDefault();
      applyShortcutFormat("_", "_");
    }

    if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "s") {
      e.preventDefault();
      applyShortcutFormat("~", "~");
    }
  };

  /* ----------------- File Upload ----------------- */
  const addFiles = (fileList) => {
    const arr = Array.from(fileList);
    const newPrev = arr.map((f) => ({
      name: f.name,
      url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
    }));

    setFiles((p) => [...p, ...arr]);
    setPreviews((p) => [...p, ...newPrev]);
  };

  const removeFile = (i) => {
    const prev = previews[i];
    if (prev?.url) URL.revokeObjectURL(prev.url);

    setFiles((p) => p.filter((_, x) => x !== i));
    setPreviews((p) => p.filter((_, x) => x !== i));
  };

  /* ----------------- SEND MESSAGE ----------------- */
  const handleSend = async () => {
    if (!text.trim() && files.length === 0) return;

    try {
      if (text.trim()) {
        const form = new FormData();
        form.append("receiverId", selectedUser._id);
        form.append("text", text.trim());

        if (replyTo?._id) form.append("replyTo", replyTo._id);

        await sendMessage(form);
      }

      for (const f of files) {
        // 1️⃣ upload file to S3
        const uploadForm = new FormData();
        uploadForm.append("file", f);

        const uploadRes = await api.post("/upload", uploadForm);

        const s3Url = uploadRes.data.url;

        // 2️⃣ send message with S3 URL
        const msgForm = new FormData();
        msgForm.append("receiverId", selectedUser._id);
        msgForm.append("file", s3Url); // ✅ IMPORTANT
        msgForm.append("fileName", f.name);
        msgForm.append("fileSize", f.size);

        if (replyTo?._id) msgForm.append("replyTo", replyTo._id);

        await sendMessage(msgForm);
      }

      setText("");
      previews.forEach((p) => p.url && URL.revokeObjectURL(p.url));
      setFiles([]);
      setPreviews([]);
      setReplyTo(null);
    } catch (err) {
      toast.error("Failed to send");
    }
  };

  /* ----------------- Helpers ----------------- */
  const autoResize = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    ta.style.height = "auto";

    const maxHeight = window.innerWidth < 768 ? 96 : 180;
    ta.style.height = Math.min(ta.scrollHeight, maxHeight) + "px";
  };

  const applyShortcutFormat = (wrapStart, wrapEnd) => {
    const t = textareaRef.current;
    if (!t) return;

    const s = t.selectionStart;
    const e = t.selectionEnd;

    const selected = text.slice(s, e);
    const newVal =
      text.slice(0, s) + wrapStart + selected + wrapEnd + text.slice(e);

    setText(newVal);

    setTimeout(() => {
      t.focus();
      t.selectionStart = s + wrapStart.length;
      t.selectionEnd = e + wrapStart.length;
    }, 10);
  };

  /* ----------------------------------------------------------
                     UI LAYOUT
  ----------------------------------------------------------- */
  return (
    <>
      <FloatingToolbar
        text={text}
        setText={setText}
        textareaRef={textareaRef}
      />

      {/* ============== REPLY PREVIEW ============== */}
      {replyTo && (
        <div
          style={{
            background: "#fff",
            borderLeft: "4px solid #0aad56",
            padding: "8px 12px",
            marginBottom: 10,
            borderRadius: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ maxWidth: "85%" }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Replying to:</div>
            <div
              style={{ fontSize: 14, color: "#444" }}
              dangerouslySetInnerHTML={{
                __html:
                  replyTo.text ||
                  replyTo.content ||
                  replyTo.fileName ||
                  "Document",
              }}
            />
          </div>

          <button
            onClick={() => setReplyTo(null)}
            style={{
              border: "none",
              background: "none",
              fontSize: 22,
              cursor: "pointer",
              marginLeft: 10,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* ============== MAIN BOX WITH DRAG & DROP ============== */}
      <form
        ref={dropRef}
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          padding: "8px 12px",
          // background: "#f0f2f5",
          borderTop: "1px solid #ddd",
          position: "relative",
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        {/* DROP OVERLAY */}
        {isDragging && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              borderRadius: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 18,
              fontWeight: 600,
              zIndex: 999,
            }}
          >
            Drop files to upload
          </div>
        )}

        {/* FILE PREVIEW ROW */}
        {previews.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 10,
              overflowX: "auto",
              paddingBottom: 4,
            }}
          >
            {previews.map((p, i) => (
              <div key={i} style={{ position: "relative", flexShrink: 0 }}>
                {p.url ? (
                  <img
                    src={p.url}
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: 10,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: 12,
                      background: "#eef",
                      borderRadius: 10,
                      minWidth: 70,
                      textAlign: "center",
                    }}
                  >
                    <FileText size={24} />
                    <div
                      style={{
                        maxWidth: 70,
                        fontSize: 11,
                        marginTop: 4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.name}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => removeFile(i)}
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    background: "#fff",
                    borderRadius: "50%",
                    border: "1px solid #ccc",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* MAIN INPUT ROW */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 14,
            background: "#fff",
            border: "1px solid #ddd",
            padding: "10px 14px",
            borderRadius: 20,
            width: "100%",
            maxWidth: "100%", // ⭐ Full responsive width
            margin: "0",
            boxSizing: "border-box",
          }}
        >
          {/* emoji */}
          <button
            type="button"
            onClick={() => setShowEmoji((v) => !v)}
            style={{ background: "none", border: "none" }}
          >
            <Smile size={22} />
          </button>
          {/* text */}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              autoResize();
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Type a message"
            rows={1}
            style={{
              flex: 1,
              width: "100%",
              minWidth: 0,
              border: "none",
              outline: "none",
              resize: "none",

              fontSize: 16,
              lineHeight: "22px",

              minHeight: 32,
              maxHeight: 96, // 🔥 mobile limit

              paddingTop: 6,
              paddingBottom: 6,

              overflowY: "auto", // 🔥 not hidden
              boxSizing: "border-box",
            }}
          />{" "}
          {/* file browser */}
          <input
            type="file"
            ref={fileRef}
            multiple
            hidden
            onChange={(e) => addFiles(e.target.files)}
          />
          <button
            type="button"
            onClick={() => fileRef.current.click()}
            style={{ background: "none", border: "none" }}
          >
            <Paperclip size={22} />
          </button>
          {/* send */}
          <button
            type="submit"
            style={{
              background: "#0aad56",
              color: "#fff",
              borderRadius: 20,
              padding: "6px 14px",
              border: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Send size={18} />
          </button>
        </div>

        {/* EMOJI PANEL */}
        {showEmoji && (
          <div
            style={{
              position: "absolute",
              bottom: "70px",
              left: 0,
              right: 0,
              margin: "auto",
              background: "#fff",
              padding: 14,
              borderRadius: 14,
              border: "1px solid #ddd",
              width: "95%",
              maxWidth: 380,
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(35px, 1fr))",
              gap: 10,
            }}
          >
            {EMOJI_BANK.map((e) => (
              <button
                key={e}
                onClick={() => {
                  setText((t) => t + e);
                  setShowEmoji(false);
                }}
                style={{
                  fontSize: 22,
                  background: "#f7f7f7",
                  border: "1px solid #eee",
                  borderRadius: 8,
                }}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </form>
    </>
  );
}
